import { useState, useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import type { Notification, NotificationListResponse } from '@mockd/shared';
import { api, ApiError } from '../utils/apiClient';

// Alerts come from mock traffic, not from anything this tab does, so poll.
// Paused while the tab is hidden; refreshed as soon as it's visible again.
const POLL_INTERVAL_MS = 60_000;

interface UseNotificationsReturn {
  notifications: Notification[];
  unreadCount: number;
  refetch: () => Promise<void>;
  setRead: (id: string, read: boolean) => Promise<void>;
  markAllRead: () => Promise<void>;
  dismiss: (id: string) => Promise<void>;
}

export function useNotifications(): UseNotificationsReturn {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  // Bumped by every mutation. A fetch that started before one returns state
  // from before it, so it's dropped rather than undoing the optimistic update.
  const mutationSeq = useRef(0);

  // Silent on failure: a background poll shouldn't toast every minute.
  const refetch = useCallback(async () => {
    const seq = mutationSeq.current;
    try {
      const result = await api.get<NotificationListResponse>('/api/notifications');
      if (seq !== mutationSeq.current) return;
      setNotifications(result.data);
      setUnreadCount(result.unreadCount);
    } catch {
      // Next poll retries.
    }
  }, []);

  useEffect(() => {
    refetch();
    const interval = setInterval(() => {
      if (!document.hidden) refetch();
    }, POLL_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (!document.hidden) refetch();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refetch]);

  // Mutations update optimistically, and on failure resync from the server
  // rather than trying to unwind the local change.
  const fail = useCallback((err: unknown, fallback: string) => {
    toast.error(err instanceof ApiError ? err.message : fallback);
    refetch();
  }, [refetch]);

  const setRead = useCallback(async (id: string, read: boolean) => {
    const target = notifications.find((n) => n.id === id);
    if (!target || (target.readAt !== null) === read) return;

    mutationSeq.current++;
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, readAt: read ? new Date().toISOString() : null } : n)),
    );
    setUnreadCount((prev) => Math.max(0, prev + (read ? -1 : 1)));
    try {
      await api.patch(`/api/notifications/${id}`, { read });
    } catch (err) {
      fail(err, 'Failed to update notification');
    }
  }, [notifications, fail]);

  const markAllRead = useCallback(async () => {
    const now = new Date().toISOString();
    mutationSeq.current++;
    setNotifications((prev) => prev.map((n) => (n.readAt ? n : { ...n, readAt: now })));
    setUnreadCount(0);
    try {
      await api.post('/api/notifications/read-all');
    } catch (err) {
      fail(err, 'Failed to mark notifications read');
    }
  }, [fail]);

  const dismiss = useCallback(async (id: string) => {
    const target = notifications.find((n) => n.id === id);
    if (!target) return;

    mutationSeq.current++;
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (!target.readAt) setUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      await api.del(`/api/notifications/${id}`);
    } catch (err) {
      fail(err, 'Failed to dismiss notification');
    }
  }, [notifications, fail]);

  return { notifications, unreadCount, refetch, setRead, markAllRead, dismiss };
}
